/**
 * IDSS (Índice de Desempenho da Saúde Suplementar) — a nota de qualidade que a
 * ANS dá a cada operadora todo ano, com as 4 dimensões. Dados abertos da ANS
 * (histórico desde 2016), carregados junto com os balancetes.
 */
import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import api from "../../services/api";
import { T, MONO, InfoTip, Card, TH, TD } from "./ui";

const DIMENSOES = [
  { chave: "idss", rotulo: "IDSS (nota geral)", peso: true,
    ajuda: "Nota de 0 a 1 que a ANS dá a cada operadora todo ano, a partir das 4 dimensões abaixo. O ano do programa avalia os dados do ano anterior (ano-base). Quanto mais perto de 1, melhor." },
  { chave: "idqs", rotulo: "Qualidade em atenção à saúde",
    ajuda: "Mede se o beneficiário recebe o cuidado certo. Exemplos de indicadores: rastreamento de câncer (mamografia, colo do útero), acompanhamento de diabéticos, consultas de idosos, partos cesáreos. Cada indicador tem um público definido (idade, sexo, condição): é onde campanhas de cuidado sobem a nota. A lista exata muda por ano: ver a ficha técnica do IDSS na ANS." },
  { chave: "idga", rotulo: "Garantia de acesso",
    ajuda: "Mede se o beneficiário consegue ser atendido. Exemplos: tamanho e distribuição da rede, uso de consultas e exames, reclamações de acesso. A lista exata muda por ano: ver a ficha técnica do IDSS na ANS." },
  { chave: "idsm", rotulo: "Sustentabilidade no mercado",
    ajuda: "Mede a sustentabilidade da operadora. Exemplos: equilíbrio econômico-financeiro, garantias exigidas pela ANS, satisfação e reclamações dos beneficiários. A lista exata muda por ano: ver a ficha técnica do IDSS na ANS." },
  { chave: "idgr", rotulo: "Gestão de processos e regulação",
    ajuda: "Mede a gestão da operadora diante da regulação. Exemplos: qualidade e regularidade das informações enviadas à ANS e cumprimento de obrigações regulatórias. A lista exata muda por ano: ver a ficha técnica do IDSS na ANS." },
];

// Faixas de cor da ANS para o IDSS
const faixa = (v) => (v === null || v === undefined ? { bg: "#f3f4f6", fg: T.inkSoft }
  : v >= 0.8 ? { bg: "#d1f2e0", fg: "#11643b" } : v >= 0.6 ? { bg: "#e6f4d7", fg: "#3c6e14" }
  : v >= 0.4 ? { bg: "#fff4cf", fg: "#7a5a00" } : v >= 0.2 ? { bg: "#fde2cc", fg: "#8a3d06" } : { bg: "#fbd5d5", fg: "#8e1b1b" });
const nota = (v) => (v === null || v === undefined ? "—" : v.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 }));
const CORES = [T.teal, "#6366f1", "#f59e0b", "#ef4444"];

const TIP_POSICAO = "Posição entre as operadoras da MESMA modalidade (ex.: autogestões) no ano mais recente. Posição = quantas têm nota maior + 1; quando várias têm a mesma nota (comum na nota máxima 1,000), aparece o empate.";

function Posicao({ p }) {
  if (!p) return null;
  return (
    <div style={{ fontSize: 10, color: T.inkSoft, marginTop: 2 }}>
      {p.posicao}º de {p.total}{p.empatadas > 1 ? ` · empate com ${p.empatadas - 1}` : ""}
    </div>
  );
}

export default function IdssComparacao({ comparar }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get("/indicadores-ans/idss", { params: { comparar: comparar || undefined } })
      .then((r) => { if (vivo) setD(r.data); }).catch(() => { if (vivo) setD({ operadoras: [] }); });
    return () => { vivo = false; };
  }, [comparar]);
  if (!d) return null;
  if (!d.ano || !d.operadoras.length) {
    return (
      <Card style={{ marginTop: 12 }}>
        <b>IDSS</b> <span style={{ fontSize: 13, color: T.inkSoft }}>ainda não carregado. Ele vem com a próxima atualização dos dados da ANS.</span>
      </Card>
    );
  }
  const ops = d.operadoras;
  const modPropria = ops[0]?.modalidade;
  const mediaMod = modPropria ? d.medias.por_modalidade?.[modPropria] : null;
  const anos = Array.from(new Set(ops.flatMap((o) => o.historico.map((h) => h.ano)))).sort();
  const serie = anos.map((a) => {
    const linha = { ano: String(a) };
    ops.forEach((o) => { const h = o.historico.find((x) => x.ano === a); linha[o.registro_ans] = h ? h.idss : null; });
    return linha;
  });
  return (
    <Card style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
        <b style={{ fontSize: 15, color: T.ink }}>IDSS {d.ano}</b>
        <span style={{ fontSize: 12, color: T.inkSoft }}>(ano-base {d.ano_base}): qualidade das operadoras segundo a ANS</span>
        <InfoTip w={360} texto="Índice de Desempenho da Saúde Suplementar, publicado pela ANS todo ano para todas as operadoras. Aqui: a nota geral, as 4 dimensões, a posição entre as operadoras da mesma modalidade e a evolução desde 2016. Cores pelas faixas da ANS (verde a partir de 0,6; verde-escuro a partir de 0,8)." />
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%" }}>
          <thead><tr>
            <th style={{ ...TH, textAlign: "left", position: "static" }}>Dimensão</th>
            {ops.map((o) => <th key={o.registro_ans} style={{ ...TH, textAlign: "center", position: "static" }}>{o.nome}<div style={{ fontWeight: 400, fontSize: 10 }}>{o.modalidade || ""}</div></th>)}
            {mediaMod && <th style={{ ...TH, textAlign: "center", position: "static" }}>Média {modPropria}</th>}
            <th style={{ ...TH, textAlign: "center", position: "static" }}>Média geral</th>
          </tr></thead>
          <tbody>
            {DIMENSOES.map((dim) => (
              <tr key={dim.chave}>
                <td style={{ ...TD, fontWeight: dim.peso ? 700 : 400 }}>
                  <span style={{ display: "inline-flex", alignItems: "center" }}>{dim.rotulo}<InfoTip w={340} texto={dim.ajuda} /></span>
                </td>
                {ops.map((o) => {
                  const v = o.atual?.[dim.chave]; const c = faixa(v);
                  return (
                    <td key={o.registro_ans} style={{ ...TD, textAlign: "center" }}>
                      <span style={{ fontFamily: MONO, fontWeight: dim.peso ? 700 : 600, background: c.bg, color: c.fg, borderRadius: 6, padding: "2px 8px" }}>{nota(v)}</span>
                      <Posicao p={o.posicao_modalidade?.[dim.chave]} />
                    </td>
                  );
                })}
                {mediaMod && <td style={{ ...TD, textAlign: "center", fontFamily: MONO, color: T.inkSoft }}>{nota(mediaMod[dim.chave])}</td>}
                <td style={{ ...TD, textAlign: "center", fontFamily: MONO, color: T.inkSoft }}>{nota(d.medias.geral?.[dim.chave])}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11, color: T.inkSoft, margin: "4px 0 10px", display: "flex", alignItems: "center" }}>
        Abaixo de cada nota: a posição entre as operadoras da mesma modalidade.<InfoTip w={320} texto={TIP_POSICAO} />
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.ink, display: "flex", alignItems: "center" }}>
        Evolução do IDSS<InfoTip w={300} texto="Nota geral ano a ano (ano do programa, que avalia o ano anterior). Mudanças de metodologia da ANS entre os anos podem mexer na nota sem mudança real no desempenho." />
      </div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={serie} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={T.line} />
            <XAxis dataKey="ano" tick={{ fontSize: 11 }} />
            <YAxis domain={[0, 1]} tick={{ fontSize: 11 }} tickFormatter={(v) => v.toLocaleString("pt-BR")} />
            <Tooltip formatter={(v) => nota(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {ops.map((o, i) => (
              <Line key={o.registro_ans} type="monotone" dataKey={o.registro_ans} name={o.nome} stroke={CORES[i % CORES.length]}
                strokeWidth={i === 0 ? 3 : 2} dot={{ r: 3 }} connectNulls />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
}
